// One player's dossier, from the Info Manager through `/api/context/player/:id`.
//
// THE CONVENTIONS IT INVENTED ARE GONE, and that is the point of this edit.
// This was the app's first real fetch, so it had to answer four questions
// nobody had answered yet — what a screen shows while a request is open, what
// it shows when the request fails, what stops a stale response from a previous
// id painting over a newer one, and what happens when the shape is wrong. It
// answered all four correctly and then four other readers answered them again,
// differently. The answers live in `data/read.ts` now; the rules they encode
// are unchanged and are quoted there:
//
//   FAIL LOUDLY. An unavailable dossier renders as an unavailable dossier,
//   with the reason the server gave. Never zeros, never dashes, never an empty
//   page that reads as "this player has nothing".
//
//   VALIDATE AT THE BOUNDARY. The response is parsed with the same Zod schema
//   the store builds it from, so a shape change upstream is a visible failure
//   here rather than an undefined halfway down a component tree.
//
//   THREE STATES, NOT TWO. Data, empty, and unavailable are different answers.
//
// The URL is still assembled by `dossierUrl` and is still THE WHOLE REQUEST,
// as a string. Depending on the request OBJECT re-ran the effect on every
// render, because a caller building it inline hands over a new one each time:
// effect → setState → render → new object → effect. The first headless walk
// of this page made 8,720 aborted requests in thirty seconds before timing out.

import { PlayerContextView } from '@fsp/domain';
import type { Dossier, DossierSection, Fact, PlayerView } from '@fsp/info';
import { useRead, type Read } from './read';

export interface DossierRequest {
  id: string;
  season: number;
  period: number;
  view?: PlayerView;
  /** A vendor namespace, when the id is somebody else's rather than ours. */
  namespace?: string;
}

/** The path, relative to `/api` — everything that identifies this request. */
export function dossierUrl(req: DossierRequest): string {
  const params = new URLSearchParams({
    season: String(req.season),
    period: String(req.period),
    view: req.view ?? 'dossier',
  });
  if (req.namespace !== undefined) params.set('ns', req.namespace);
  return `/context/player/${encodeURIComponent(req.id)}?${params.toString()}`;
}

export function useDossier(req: DossierRequest | null): Read<PlayerContextView | null> {
  return useRead(req === null ? null : dossierUrl(req), PlayerContextView);
}

// ── Reading a dossier ───────────────────────────────────────────────────────
//
// A dossier is not a bag of facts about one player. It is facts about a player,
// his team, his fixture, his stadium and the ten people beside him on the
// injury report — and every one of them arrives in the same shape.
//
// So the only safe way to pull "his injury" out of it is to ask who the fact is
// ABOUT, which `Fact.refs` says. Flattening the sections and taking the first
// match does not do that, and the failure is silent and specific: a player with
// no report of his own gets the first TEAMMATE's report instead. Travis Kelce
// rendered a defensive tackle's calf and "Did Not Participate In Practice"
// while being entirely healthy.
//
// Two helpers, because there are genuinely two questions:
//
//   ownFact  — "what does the store say about HIM": his profile, his roster
//              row, his injury. The subject ref must be him.
//   factIn   — "what does the store say about his WEEK": the fixture, the
//              forecast, the stadium. Those are facts about a contest or a
//              venue, so the subject is never the player; the section they came
//              from is what scopes them to him.
//
// Never reintroduce a flattened lookup. The four environment kinds are safe
// today only because `teammates` happens to carry nothing but injury reports,
// and that is luck, not a guarantee.

/** Every fact of a kind whose subject is the dossier's own entity. */
export function ownFacts(d: Dossier, kind: string): Fact[] {
  return Object.values(d.sections)
    .flatMap((s) => s.facts)
    .filter(
      (f) =>
        f.kind === kind && f.refs.some((r) => r.role === 'subject' && r.entityId === d.entity.id),
    );
}

/** His current claim of a kind — the store orders newest first. */
export function ownFact(d: Dossier, kind: string): Fact | undefined {
  return ownFacts(d, kind)[0];
}

/** The current fact of a kind from ONE named section, about him or his week. */
export function factIn(d: Dossier, section: DossierSection, kind: string): Fact | undefined {
  return d.sections[section]?.facts.find((f) => f.kind === kind);
}
